import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './features/auth/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorHandlerComponent } from './shared/components/error-handler/error-handler.component';
import {
  AfterViewInit,
  Component,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Router,
  NavigationEnd,
  RoutesRecognized,
  RouterOutlet,
} from '@angular/router';
import { CanFindLocalizedRouteGuard } from './can-find-localized-route.guard';
import { Location } from '@angular/common';
import {
  MatSidenav,
  MatSidenavContent,
  MatSidenavModule,
} from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { HeaderService } from './features/header/header.service';
import { TranslateRouterLinkPipe } from './core';
import { GtmService } from './core/services/gtm.service';
import { IdleComponent } from './features/auth/idle/idle.component';
import { FooterComponent } from './features/footer/footer.component';
import { HeaderComponent, SideNavComponent } from './features/header';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    MatSidenavModule,
    TranslateModule,
    IdleComponent,
    HeaderComponent,
    SideNavComponent,
    FooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class AppComponent implements AfterViewInit {
  title = 'TTInteractive';
  navItems: any[] = [];
  @ViewChild(MatSidenav) sidenav: MatSidenav | undefined;
  @ViewChild(MatSidenavContent) sidenavContent: MatSidenavContent | undefined;
  @ViewChild('scrollToRef') scrollToRef: ElementRef | undefined;

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    public readonly translateSevice: TranslateService,
    private readonly titleService: Title,
    public readonly headerService: HeaderService,
    private readonly authService: AuthService,
    private readonly translateRouterLink: TranslateRouterLinkPipe,
    private readonly cookieService: CookieService,
    private readonly gtmService: GtmService
  ) {
    // We must add the 404 route here to avoid this going through
    // localize-router in the app-routing.module.ts file.
    // Otherwise the catch-all would be under a locale too
    this.router.config?.push({
      path: '**',
      component: ErrorHandlerComponent,
      data: { code: 400, titleRef: 'SHARED.PAGE-TITLES.ERROR' },
      canActivate: [CanFindLocalizedRouteGuard],
    });

    this.subscribeRouter();
    this.subscribeNavChanges();
    this.handleUserOnPageRefresh();
  }

  ngAfterViewInit(): void {
    // For scroll
    setTimeout(() => {
      this.sidenavContent?.elementScrolled().subscribe((event: any) => {
        let elClassList = this.scrollToRef?.nativeElement.classList;
        let currentTopOffset = this.sidenavContent?.measureScrollOffset('top');
        if (
          !elClassList.contains('show-scrollTop') &&
          currentTopOffset &&
          currentTopOffset > 50
        ) {
          elClassList.add('show-scrollTop');
        } else if (currentTopOffset == 0) {
          elClassList.remove('show-scrollTop');
        }
      });
    });
  }

  handleUserOnPageRefresh() {
    if (!this.location.path()) return;
    const currentPath = this.location.path()?.split(/(#?;)+/)[0];
    let token = currentPath.split('/en/sso?token=')[1];
    if (!token) {
      token = this.cookieService.get(this.authService.tokenName);
    }
    if (!token) return;
    this.authService.getUserByToken(token).subscribe((userInfoRes: any) => {
      this.authService.authenticateUserByJwtToken(userInfoRes);
      if (userInfoRes?.userId) {
        this.redirectToCorrectLandingPage(userInfoRes, currentPath);
      }
    });
  }

  redirectToCorrectLandingPage(userInfoRes: any, currentPath: string) {
    let redirectUrl = currentPath || this.authService.redirectUrl || '/home';
    if (currentPath.indexOf('/sso?token=') > -1) {
      redirectUrl = '/tracking';
      if (
        userInfoRes.isAirOceanWebActive &&
        !userInfoRes.isOtherTtslsUnitWebActive
      ) {
        redirectUrl = '/air-ocean';
      }
      if (
        userInfoRes.isWarehouseUnitWebActive &&
        !userInfoRes.isOtherTtslsUnitWebActive &&
        !userInfoRes.isAirOceanWebActive
      ) {
        redirectUrl = '/warehouse';
      }
    }
    this.router.navigate(this.translateRouterLink.transform(redirectUrl));
  }

  subscribeRouter() {
    if (this.router.events)
      this.router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          // GTM page view
          this.gtm.pageViewEventHandler(event);
        }
        if (event instanceof RoutesRecognized) {
          this.setDynamicPageTitle(event);
        }
      });
  }

  setDynamicPageTitle(event: RoutesRecognized) {
    let pageTitle = 'Interactive Service';
    let childRoute = event.state.root.firstChild;
    let titleRef = childRoute?.data?.titleRef;
    while (!titleRef && childRoute) {
      childRoute = childRoute?.firstChild || null;
      titleRef = childRoute?.data?.titleRef;
    }
    if (titleRef) {
      pageTitle = this.translateSevice.instant(titleRef);
      pageTitle = pageTitle === titleRef ? 'Interactive Service' : pageTitle;
    }
    this.titleService.setTitle(pageTitle);
  }

  subscribeNavChanges() {
    this.headerService.isSideNavOpen$
      .asObservable()
      .subscribe((isOpen: boolean) => {
        if (isOpen && !this.sidenav?.opened) {
          this.sidenav?.open();
        } else if (!isOpen) {
          this.sidenav?.close();
        }
      });
  }

  onScrollToTop() {
    document.getElementById('top-div')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }

  gtm = {
    pageViewEventHandler: (item: any) => {
      const gtmTag = {
        event: 'page',
        pageName: item.url,
      };
      this.gtmService.pushTag(gtmTag);
    },
  };

  @HostListener('document:keydown.tab', ['$event']) onKeydownHandler(
    _event: Event
  ) {
    document.body.classList.add('is-tabbing');
  }
  @HostListener('document:click', ['$event']) onClickHandler(_event: Event) {
    document.body.classList.remove('is-tabbing');
  }
}
